<template>
  <div
    class="cf-node"
    @focusout="onFocusOut"
  >
    <!-- Node header -->
    <div class="cf-header">
      <feather-icon
        :icon="node.isexpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
        size="12"
        class="cf-toggle"
        @click="node.isexpanded = !node.isexpanded"
      />
      <span
        class="cf-dot"
        :class="node.isactive ? 'cf-dot--on' : 'cf-dot--off'"
        @click="node.isactive = !node.isactive"
      />
      <span
        class="cf-oper-btn"
        @click="switchOper"
      >
        {{ node.oper.sid === 'and' ? 'И' : 'ИЛИ' }}
      </span>
      <span
        class="cf-add"
        @click="addCond"
      >
        <feather-icon
          icon="PlusIcon"
          size="10"
        /> условие
      </span>
      <span
        class="cf-add"
        @click="addNode"
      >
        <feather-icon
          icon="PlusIcon"
          size="10"
        /> группа
      </span>
      <span
        v-if="!root"
        class="cf-del"
        @click="$emit('delete')"
      >
        <feather-icon
          icon="TrashIcon"
          size="10"
        />
      </span>
    </div>

    <!-- Node body -->
    <div
      v-if="node.isexpanded"
      class="cf-body"
    >
      <!-- Conditions -->
      <div
        v-for="(cond, ci) in node.conds"
        :key="'c' + ci"
        class="cf-line"
      >
        <!-- condition active toggle -->
        <span
          class="cf-dot"
          :class="cond.isactive ? 'cf-dot--on' : 'cf-dot--off'"
          @click="cond.isactive = !cond.isactive"
        />

        <!-- arg0 -->
        <cbs-inline-argument
          :arg="cond.args[0]"
          :arg-types="argTypes"
          :fields="fields"
          :param-fields="paramFields"
          :all-entities="allEntities"
          :refs="refs"
          @change="e => onArgChange(ci, 0, e)"
          @ensure-ref="ensureRef"
        />

        <!-- operator -->
        <span
          class="cf-word cf-word--oper"
          @click="openPicker(ci)"
        >{{ operLabel(cond) }}</span>

        <!-- arg1 (if 2-arg operator) -->
        <cbs-inline-argument
          v-if="cond.oper.args === 2 && cond.args.length > 1"
          :arg="cond.args[1]"
          :arg-types="argTypes"
          :fields="fields"
          :param-fields="paramFields"
          :all-entities="allEntities"
          :refs="refs"
          @change="e => onArgChange(ci, 1, e)"
          @ensure-ref="ensureRef"
        />

        <!-- delete condition button -->
        <span
          class="cf-del"
          @click="removeCond(ci)"
        >
          <feather-icon
            icon="XIcon"
            size="10"
          />
        </span>

        <!-- operator picker dropdown -->
        <div
          v-if="activeRow === ci"
          class="cf-panel"
        >
          <b-form-input
            :ref="'pi_' + ci"
            v-model="search"
            size="sm"
            placeholder="Поиск..."
            autocomplete="off"
            @input="onSearch"
            @keydown.esc="closePicker"
            @keydown.enter.prevent="pickFirst(ci)"
          />
          <div
            v-if="filtered.length"
            class="cf-list"
          >
            <div
              v-for="item in filtered"
              :key="item.value"
              class="cf-opt"
              @click.stop="pick(ci, item)"
            >{{ item.text }}</div>
          </div>
          <div
            v-else-if="search"
            class="cf-empty"
          >
            Не найдено
          </div>
        </div>
      </div>

      <!-- Child nodes (recursive) -->
      <cbs-filter-constructor
        v-for="(sub, ni) in node.nodes"
        :key="'n' + ni"
        :node="sub"
        :fields="fields"
        :param-fields="paramFields"
        :root="false"
        @delete="removeNode(ni)"
      />
    </div>
  </div>
</template>

<script>
import { BFormInput } from 'bootstrap-vue'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'
import store from '@/store/index'
import CbsInlineArgument from '@/cubus/components/inline-argument/CbsInlineArgument.vue'

let cachedOpers = null
let opersLoading = false
let opersCallbacks = []

let cachedArgTypes = null
let argTypesLoading = false
let argTypesCallbacks = []

let cachedAllEntities = null
let allEntitiesLoading = false
let allEntitiesCallbacks = []

export default {
  name: 'CbsFilterConstructor',
  components: { BFormInput, CbsInlineArgument },
  props: {
    node: { type: Object, required: true },
    fields: { type: Array, default: () => [] },
    paramFields: { type: Array, default: () => [] },
    root: { type: Boolean, default: true },
  },
  data() {
    return {
      opers: cachedOpers || [
        {
          id: 1, sid: 'equal', name: '=', args: 2,
        },
        {
          id: 2, sid: 'notequal', name: '<>', args: 2,
        },
      ],
      argTypes: cachedArgTypes || [
        { id: 1, sid: 'entity', name: 'Entity' },
        { id: 2, sid: 'value', name: 'Value' },
      ],
      allEntities: cachedAllEntities || [],
      refs: {},
      activeRow: -1,
      search: '',
      source: [],
      filtered: [],
    }
  },
  created() {
    if (!cachedOpers) this.loadOpers()
    if (!cachedArgTypes) this.loadArgTypes()
    if (!cachedAllEntities) this.loadAllEntities()
    this.loadRefsForExisting()
  },
  mounted() {
    this._outsideClick = e => {
      if (this.activeRow >= 0 && !e.target.closest('.cf-line')) this.closePicker()
    }
    document.addEventListener('click', this._outsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this._outsideClick)
  },
  methods: {
    onFocusOut(e) {
      if (this.$el.contains(e.relatedTarget)) return
      this.$emit('save')
    },

    operLabel(cond) {
      if (!cond.oper) return '(оператор)'
      if (cond.oper.name) return cond.oper.name
      const sidMap = {
        equal: '=', notequal: '≠', greater: '>', less: '<', greaterequal: '≥', lessequal: '≤', like: 'содержит', isnull: 'пусто', isnotnull: 'не пусто', in: 'в списке', between: 'между', startwith: 'начинается', exists: 'существует', inbranch: 'в ветке', currentuser: 'тек.пользователь',
      }
      return sidMap[cond.oper.sid] || cond.oper.sid || '(оператор)'
    },

    // cross-arg sync when arg0 entity is picked
    onArgChange(ci, argIdx, { kind, field, preserveType }) {
      if (kind === 'entity' && argIdx === 0) {
        const cond = this.node.conds[ci]
        if (!preserveType && cond.args.length > 1 && cond.args[1].type.sid === 'value' && field) {
          const valArg = cond.args[1]
          valArg.datatype = field.datatype
          valArg.entityid = field.value
          valArg.entitysid = field.entitysid
          valArg.parententityid = field.parententityid
          valArg.value = null
          if ((field.datatype === 'ref' || field.datatype === 'refsid') && field.value) {
            this.ensureRef(field.value)
          }
        }
      }
    },

    // --- node operations ---
    switchOper() {
      if (this.node.oper.sid === 'and') {
        this.node.oper.id = 2; this.node.oper.sid = 'or'; this.node.oper.name = 'OR'
      } else {
        this.node.oper.id = 1; this.node.oper.sid = 'and'; this.node.oper.name = 'AND'
      }
    },
    addCond() {
      this.node.conds.push({
        isactive: true,
        name: `cond ${this.node.conds.length + 1}`,
        oper: {
          id: 1, sid: 'equal', name: '=', args: 2,
        },
        args: [
          {
            type: { id: 1, sid: 'entity', name: 'Entity' }, datatype: null, entityid: null, entitysid: null, parententityid: null, value: null,
          },
          {
            type: { id: 2, sid: 'value', name: 'Value' }, datatype: null, entityid: null, entitysid: null, parententityid: null, value: null,
          },
        ],
      })
      this.node.isactive = true
      this.node.isexpanded = true
    },
    addNode() {
      this.node.nodes.push({
        isactive: false,
        isexpanded: true,
        oper: { id: 1, sid: 'and', name: 'AND' },
        nodes: [],
        conds: [],
      })
      this.node.isexpanded = true
    },
    removeCond(i) { this.node.conds.splice(i, 1); if (this.activeRow === i) this.closePicker(); this.$emit('save') },
    removeNode(i) { this.node.nodes.splice(i, 1); this.$emit('save') },

    // --- operator picker ---
    openPicker(ci) {
      if (this.activeRow === ci) { this.closePicker(); return }
      this.search = ''
      this.source = this.opers.map(o => ({ value: o.sid, text: o.name, _o: o }))
      this.filtered = this.source
      this.activeRow = ci
      this.$nextTick(() => {
        const ref = this.$refs[`pi_${ci}`]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el) el.focus()
      })
    },
    closePicker() {
      this.activeRow = -1
      this.search = ''
      this.source = []
      this.filtered = []
    },
    onSearch() {
      if (!this.search.trim()) { this.filtered = this.source; return }
      const parts = this.search.trim().toLowerCase().split(/\s+/)
      const re = new RegExp(parts.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*'))
      this.filtered = this.source.filter(i => re.test((i.text || '').toLowerCase()))
    },
    pickFirst(ci) { if (this.filtered.length) this.pick(ci, this.filtered[0]) },
    pick(ci, item) {
      const cond = this.node.conds[ci]
      if (item._o) this.$set(cond, 'oper', { ...item._o })
      this.closePicker()
    },

    // --- reference loading ---
    async ensureRef(entityid) {
      if (!entityid || this.refs[entityid]) return
      this.$set(this.refs, entityid, [])
      const ref = await useCubus.loadRef(this, { entityid })
      if (ref) {
        this.$set(this.refs, entityid, ref.map(r => ({
          sid: r.sid, value: r.value, title: r.title && r.title !== '' ? r.title : `(${r.value})`,
        })))
      }
    },
    loadRefsForExisting() {
      if (!this.node.conds) return
      this.node.conds.forEach(cond => {
        if (cond.args && cond.args[1]) {
          const a = cond.args[1]
          if ((a.datatype === 'ref' || a.datatype === 'refsid') && a.entityid) this.ensureRef(a.entityid)
        }
      })
    },

    // --- load opers ---
    loadOpers() {
      if (opersLoading) { opersCallbacks.push(o => { this.opers = o }); return }
      opersLoading = true
      useJwt.query({ query: { method: 'conditionoperands', param: {} } })
        .then(r => { if (r.data.thread) this.pollOpers(r.data.thread) })
        .catch(() => { opersLoading = false })
    },
    pollOpers(thread) {
      setTimeout(() => {
        useJwt.query({ query: { method: 'thread', param: { threadname: thread } } })
          .then(r => {
            if (r.data.thread && r.data.thread.status === 'done') {
              const { opers } = r.data.thread.result.result
              cachedOpers = opers; this.opers = opers; opersLoading = false
              opersCallbacks.forEach(cb => cb(opers)); opersCallbacks = []
            } else { this.pollOpers(thread) }
          })
          .catch(() => { opersLoading = false })
      }, 500)
    },

    // --- load arg types ---
    loadArgTypes() {
      const st = store.getters['cubus-store/ARGUMENT_TYPES']
      if (st) { cachedArgTypes = st; this.argTypes = st; return }
      if (argTypesLoading) { argTypesCallbacks.push(t => { this.argTypes = t }); return }
      argTypesLoading = true
      useJwt.query({ query: { method: 'argumenttypes', param: {} } })
        .then(r => { if (r.data.thread) this.pollArgTypes(r.data.thread) })
        .catch(() => { argTypesLoading = false })
    },
    pollArgTypes(thread) {
      setTimeout(() => {
        useJwt.query({ query: { method: 'thread', param: { threadname: thread } } })
          .then(r => {
            if (r.data.thread && r.data.thread.status === 'done') {
              const { types } = r.data.thread.result
              cachedArgTypes = types; this.argTypes = types
              store.commit('cubus-store/SET_ARGUMENT_TYPES', types)
              argTypesLoading = false; argTypesCallbacks.forEach(cb => cb(types)); argTypesCallbacks = []
            } else { this.pollArgTypes(thread) }
          })
          .catch(() => { argTypesLoading = false })
      }, 500)
    },

    // --- load all entities ---
    async loadAllEntities() {
      if (cachedAllEntities) { this.allEntities = cachedAllEntities; return }
      if (allEntitiesLoading) { allEntitiesCallbacks.push(e => { this.allEntities = e }); return }
      allEntitiesLoading = true
      try {
        const query = await useCubus.loadQuery(this, { objectsid: 'entity', limit: 10000, offset: 0 })
        if (query && query.recordset) {
          const entities = (query.recordset.records || []).map(rec => {
            const c = rec.cells || rec
            return {
              value: c.id ? c.id.value : null,
              title: c.name ? c.name.value : '',
              entitysid: c.sid ? c.sid.value : '',
              datatype: null,
              parententityid: null,
              refobjectid: null,
            }
          }).filter(e => e.value)
          cachedAllEntities = entities
          this.allEntities = entities
          allEntitiesCallbacks.forEach(cb => cb(entities))
          allEntitiesCallbacks = []
        }
      } finally {
        allEntitiesLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.cf-node {
  font-size: 12px;
}

.cf-header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;
  user-select: none;
}

.cf-toggle {
  color: #b9b9c3;
  cursor: pointer;
  flex-shrink: 0;
}

.cf-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  cursor: pointer;
}
.cf-dot--on { background: #28c76f; }
.cf-dot--off { background: #ea5455; }

.cf-oper-btn {
  font-weight: 600;
  font-size: 11px;
  color: #7367f0;
  cursor: pointer;
  padding: 1px 5px;
  border: 1px solid #7367f0;
  border-radius: 3px;
  line-height: 1.3;
  &:hover { background: rgba(115, 103, 240, 0.1); }
}

.cf-add {
  font-size: 11px;
  color: #b9b9c3;
  cursor: pointer;
  padding: 1px 4px;
  border-radius: 3px;
  white-space: nowrap;
  &:hover { color: #28c76f; background: rgba(40, 199, 111, 0.1); }
}

.cf-del {
  color: #b9b9c3;
  cursor: pointer;
  padding: 1px 3px;
  border-radius: 3px;
  line-height: 1;
  flex-shrink: 0;
  &:hover { color: #ea5455; background: rgba(234, 84, 85, 0.1); }
}

.cf-body {
  padding-left: 16px;
}

.cf-line {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
  line-height: 1.4;
  flex-wrap: wrap;
}

.cf-word--oper {
  font-weight: 600;
  color: #ff9f43;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  border-bottom: 1px dashed rgba(255, 159, 67, 0.4);
  &:hover { border-bottom-color: #ff9f43; }
}

.cf-panel {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 10;
  width: 220px;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 4px;
}

.cf-list {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
}

.cf-opt {
  padding: 3px 8px;
  cursor: pointer;
  border-radius: 3px;
  color: #5e5873;
  &:hover { background: #f8f8f8; }
}

.cf-empty {
  padding: 6px 8px;
  color: #b9b9c3;
}

// Dark mode
.dark-layout {
  .cf-toggle { color: #4b4b5a; }
  .cf-add { color: #4b4b5a; }
  .cf-del { color: #4b4b5a; }
  .cf-oper-btn { color: #7367f0; border-color: #7367f0; }
  .cf-word--oper { color: #ff9f43; }
  .cf-panel {
    background: #283046;
    border-color: #3b4253;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  .cf-opt {
    color: #d0d2d6;
    &:hover { background: #3b4253; }
  }
}
</style>
